"use client";

import { useState, useRef, useEffect } from "react";
import Image from "next/image";
import Slider from "react-slick";
import { ChevronLeft, ChevronRight, MoveUpRight, Move } from "lucide-react";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { getImageUrl } from "@/utils/utils";
import Link from "next/link";

const ProjectsSlider = ({ projects }) => {
  const [currentSlide, setCurrentSlide] = useState(4);
  const [isMobile, setIsMobile] = useState(false);
  const sliderRef = useRef<Slider>(null);

  // Device detection function
  const detectDevice = () => {
    const width = window.innerWidth;
    const isMobileDevice = width <= 768;

    setIsMobile(isMobileDevice);
  };

  // Effect to detect device on mount and resize
  useEffect(() => {
    // Initial detection
    detectDevice();

    // Add resize listener
    window.addEventListener("resize", detectDevice);

    // Cleanup
    return () => window.removeEventListener("resize", detectDevice);
  }, []);

  // Slider settings
  const sliderSettings = {
    centerMode: !isMobile,
    centerPadding: "0px",
    slidesToShow: isMobile ? 1 : 6,
    slidesToScroll: 1,
    infinite: true,
    speed: 500,
    arrows: false,
    dots: false,
    initialSlide: 4,
    swipe: true,
    variableWidth: !isMobile,
    beforeChange: (oldIndex: number, newIndex: number) => {
      setCurrentSlide(newIndex);
    },
  };

  const goToPrev = () => {
    sliderRef.current?.slickPrev();
  };

  const goToNext = () => {
    sliderRef.current?.slickNext();
  };

  return (
    <div className="relative w-full flex items-center justify-center min-h-[400px] sm:min-h-[450px] lg:h-[500px]">
      {/* Custom Navigation Arrows - Responsive positioning */}
      {/* Left Arrow - Desktop: 50% on focus item, Mobile: Left side */}
      <button
        onClick={goToPrev}
        className="cursor-pointer absolute top-1/2 -translate-y-1/2 w-10 h-10 sm:w-12 sm:h-12 bg-[#1a5852] rounded-full flex items-center justify-center transition-all duration-300 hover:bg-[#194843] shadow-lg z-40 left-4 lg:left-[calc(50%-190px)]"
        aria-label="Previous project"
      >
        <ChevronLeft size={16} color="white" className="sm:w-5 sm:h-5" />
      </button>

      {/* Right Arrow - Desktop: 50% on focus item, Mobile: Right side */}
      <button
        onClick={goToNext}
        className="cursor-pointer absolute top-1/2 -translate-y-1/2 w-10 h-10 sm:w-12 sm:h-12 bg-[#1a5852] rounded-full flex items-center justify-center transition-all duration-300 hover:bg-[#194843] shadow-lg z-40 right-4 lg:right-[calc(50%-190px)]"
        aria-label="Next project"
      >
        <ChevronRight size={16} color="white" className="sm:w-5 sm:h-5" />
      </button>

      {/* Slick Carousel - Full Width */}
      <div className="w-full projects-slider-container">
        <Slider ref={sliderRef} {...sliderSettings}>
          {projects.map((project, index) => {
            const total = projects.length;
            let dist = Math.abs(index - currentSlide);
            if (dist > total / 2) dist = total - dist;

            let heightClass = "";
            if (dist === 0) {
              heightClass = "h-[400px]";
            } else if (dist === 1) {
              heightClass = "h-[382px]";
            } else if (dist === 2) {
              heightClass = "h-[265px]";
            } else {
              heightClass = "h-[180px]"; // fallback for far slides
            }

            const isCenter = dist === 0;

            return (
              <Link
                key={project.id}
                href={
                  currentSlide === index ? `/en/projects/${project.slug}` : ""
                }
                className={
                  currentSlide === index
                    ? "cursor-pointer"
                    : "cursor-default pointer-events-none"
                }
              >
                <div
                  className={`transition-all duration-500 ease-out flex flex-col items-center ${
                    isCenter
                      ? "transform scale-110 pt-8 pb-13 mt-8 -mb-8"
                      : "transform scale-90 opacity-70 pt-8 pb-8"
                  }`}
                >
                  <div
                    className={`relative overflow-hidden rounded-xl shadow-xl mx-auto w-full max-w-[300px] sm:w-[350px] lg:w-[350px] ${heightClass} group`}
                  >
                    {project?.image && (
                      <Image
                        src={getImageUrl(project?.image?.url || "")}
                        alt={project.image?.name}
                        fill
                        className="object-cover rounded-xl"
                        draggable={false}
                      />
                    )}
                    {/* Overlay for non-center slides */}
                    {!isCenter && (
                      <div className="absolute inset-0 bg-black/30 transition-all duration-500" />
                    )}
                    {/* Top-right link arrow icon, only visible on focus item hover */}
                    <span
                      tabIndex={-1}
                      className={`absolute top-3 right-3 z-20 w-8 h-8 flex items-center justify-center bg-white rounded-full shadow-md
                        opacity-0 scale-0 pointer-events-none
                        ${isCenter ? "group-hover:opacity-100 group-hover:scale-100 group-hover:pointer-events-auto" : ""}
                        transition-all duration-300`}
                      aria-label="View project details"
                    >
                      <MoveUpRight size={16} color="#1a5852" />
                    </span>
                  </div>
                  <div
                    className={`text-center mt-4 sm:mt-6 px-2 transition-all duration-500 line-clamp-2 ${
                      isCenter
                        ? "text-[#1A1D21] font-inter text-[14px] sm:text-[16px] font-normal leading-[20px] sm:leading-[24px]"
                        : "text-gray-500 text-sm sm:text-base font-medium"
                    }`}
                  >
                    {project.title}
                  </div>
                </div>
              </Link>
            );
          })}
        </Slider>
      </div>
    </div>
  );
};

export default ProjectsSlider;
